import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Checkbox as MUICheckbox,
  TextField,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { queryClient } from 'src'
import { TextInputField } from 'src/components/forms/text-input-field/text-input-field'
import DatePickerField from '~components/forms/date-picker-field/date-picker-field'
import { SelectField } from '~components/forms/select-field/select-field'
import { AddIcon, EditIcon, LockIcon } from '~components/icons'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { FileBox } from '~components/molecules/file-box/file-box'
import ConfirmModal from '~components/molecules/modal/confirm-modal/confirm-modal'
import {
  ModalFooter,
  StyledInput,
} from '~components/molecules/modal/modal.styles'
import { Checkbox, TextButton, Typography } from '~components/web-components'
import { useDeleteDocument } from '~hooks/queries/documents/use-delete-document'
import { usePostDocuments } from '~hooks/queries/documents/use-post-documents'
import { IDocumentsRowData } from '~hooks/queries/vessels/documents/types'
import { useGetVessel } from '~hooks/queries/vessels/use-get-vessel'
import { useDateFormat } from '~hooks/use-date-format'
import { useMount } from '~hooks/use-mount'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { COLORS } from '~theme/colors'
import { Z_INDEX } from '~types/z-index'
import {
  forceDateToZuluTimezone,
  parseDateOnly,
  toDateFormat,
} from '~utils/dates'
import { gtm } from '~utils/gtm'
import { invalidInputMessage } from '~utils/invalid-input-message'
import { useCanEditAndAddDocument } from '../documents-table/documents-table-mui'
import {
  certificateCategories,
  certificateIssuerOptions,
} from './add-document-modal'
import { DocumentTagsField } from './document-tags-field'

interface ITableValuesEditModalContent {
  document: IDocumentsRowData
  isNaChecked?: boolean
  selectedCategories: string[]
  setSelectedCategories: (categories: string[]) => void
  showTechManagerDocuments: boolean
  existingDocumentTags: string[]
}

const getIsDateRequired = (type: string) =>
  type !== 'Secondary Certificates' &&
  type !== 'Primary Certificates (Other)' &&
  type.toLowerCase().includes('certificate')

const TableValuesEditModalContent: FC<ITableValuesEditModalContent> = ({
  document,
  isNaChecked,
  selectedCategories,
  setSelectedCategories,
  showTechManagerDocuments,
  existingDocumentTags,
}) => {
  const [tags, setTags] = useState<string[]>(document.tags || [])
  const documentName = document.documentName
  const type = document.type
  const issueDate = document.issued
  const expiryDate = document.expires
  const isQ88 = document.source === 'Q88'
  const certificateNumber = document.certificateNumber
  const referenceNumber = document.referenceNumber
  const certificateIssuer = document.certificateIssuer
  const lastSurvey = document.lastSurvey
  const nextSurvey = document.nextSurvey
  const remarks = document.remarks

  const { register, setValue, clearErrors, watch } = useFormContext()
  const isDigitalCertificate = watch('digitalCertificate')
  const noExpiryDate = watch('noExpiryDate')
  const noNextSurvey = watch('noNextSurvey')
  const status =
    document.files?.length && !isQ88 ? 'uploaded' : 'empty_vetting_document'

  const fileLabel = `${type} Document`

  const isDateRequired = getIsDateRequired(type)

  return (
    <>
      {showTechManagerDocuments && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            maxWidth: '799px',
            mb: 4,
          }}
        >
          <Checkbox
            {...register('digitalCertificate')}
            label="Digital/Electronic Certificate"
            name="digitalCertificate"
            checked={isDigitalCertificate}
            onChange={(e) =>
              setValue(
                'digitalCertificate',
                Boolean((e.target as HTMLInputElement).checked),
              )
            }
          />
          <FormControlLabel
            control={
              <MUICheckbox
                checked={!!noExpiryDate}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('expires', null)
                  }
                  setValue('noExpiryDate', e.target.checked)
                }}
              />
            }
            label="No Expiry Date"
          />
          <FormControlLabel
            control={
              <MUICheckbox
                checked={!!noNextSurvey}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('nextSurvey', null)
                  }
                  setValue('noNextSurvey', e.target.checked)
                }}
              />
            }
            label="No Next Survey"
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          mb: 4,
        }}
      >
        <StyledInput
          name="type"
          required
          value={`${type} Documents`}
          readOnly
          icon={LockIcon}
        />
        <StyledInput
          name="documentName"
          required
          value={documentName}
          readOnly
          icon={LockIcon}
        />
      </Box>

      {showTechManagerDocuments && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            mb: 4,
          }}
        >
          <Autocomplete
            limitTags={1}
            size="small"
            multiple
            disableCloseOnSelect
            options={certificateCategories}
            value={selectedCategories}
            onChange={(_, newValue) => {
              setSelectedCategories(newValue)
              setValue('certificateCategories', newValue)
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Document Categories" />
            )}
            sx={{ minWidth: 344 }}
            slotProps={{ popper: { style: { zIndex: Z_INDEX.Popup } } }}
          />
        </Box>
      )}

      {showTechManagerDocuments && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            mb: 4,
          }}
        >
          <SelectField
            name="certificateIssuer"
            options={certificateIssuerOptions}
            placeholder="Certificate Issuer"
            defaultValue={certificateIssuer || ''}
          />
          <TextInputField
            sx={{ width: 344 }}
            name="certificateNumber"
            label="Certificate Number"
            defaultValue={certificateNumber || ''}
          />
        </Box>
      )}

      {showTechManagerDocuments && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            mb: 4,
          }}
        >
          <TextInputField
            sx={{ width: 344 }}
            name="referenceNumber"
            label="Reference number to physical filing system"
            defaultValue={referenceNumber || ''}
          />
        </Box>
      )}

      {!showTechManagerDocuments && (
        <Checkbox
          {...register('notApplicable')}
          label="Not applicable"
          name="notApplicable"
          checked={isNaChecked}
          onChange={(e) =>
            setValue(
              'notApplicable',
              Boolean((e.target as HTMLInputElement).checked),
            )
          }
        />
      )}

      {!isNaChecked && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            my: 4,
          }}
        >
          <DatePickerField
            name="issued"
            label="Issue Date"
            required={isDateRequired}
            selected={
              isQ88
                ? undefined
                : issueDate
                  ? parseDateOnly(issueDate)
                  : undefined
            }
          />
          {!noExpiryDate && (
            <DatePickerField
              name="expires"
              required={isDateRequired}
              label="Expiry Date"
              selected={
                isQ88
                  ? undefined
                  : expiryDate
                    ? parseDateOnly(expiryDate)
                    : undefined
              }
            />
          )}
        </Box>
      )}

      {showTechManagerDocuments && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            mb: 4,
          }}
        >
          <DatePickerField
            name="lastSurvey"
            label="Last Survey"
            selected={lastSurvey ? parseDateOnly(lastSurvey) : undefined}
          />
          {!noNextSurvey && (
            <DatePickerField
              name="nextSurvey"
              label="Next Survey"
              selected={nextSurvey ? parseDateOnly(nextSurvey) : undefined}
            />
          )}
        </Box>
      )}

      {showTechManagerDocuments && (
        <Box sx={{ mb: 4 }}>
          <DocumentTagsField
            documentTags={tags}
            existingDocumentTags={existingDocumentTags}
            onChange={(newTags) => {
              setTags(newTags)
              setValue('tags', newTags)
            }}
          />
        </Box>
      )}

      {showTechManagerDocuments && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <TextInputField
            multiline
            minRows={4}
            maxRows={4}
            sx={{ width: 714 }}
            name="remarks"
            label="Remarks"
            defaultValue={remarks || ''}
          />
        </Box>
      )}

      {!isNaChecked && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mt: 1 }}>
            <FileBox
              style={{ width: '100%' }}
              {...(register('file') as unknown as any)}
              name={document.files?.[0]?.filename || 'file'}
              status={status}
              isLoading={false}
              isDirectUpload={false}
              onChange={(file?: File) => {
                if (file) {
                  setValue('file', file)
                  clearErrors('file')
                }
              }}
              onRemove={() => {
                setValue('file', undefined)
              }}
              isApprovalRequired={showTechManagerDocuments}
              canEdit
            />
          </Box>
        </Box>
      )}
    </>
  )
}

interface IDocumentsEditModal {
  canAddDocument?: boolean
  close?: () => void
  document: IDocumentsRowData
  refetch?: () => void
  existingDocumentTags?: string[]
  showTechManagerDocuments: boolean
}

export const DocumentsEditModalWithTrigger: FC<IDocumentsEditModal> = ({
  close,
  document,
  canAddDocument,
  refetch,
  existingDocumentTags = [],
  showTechManagerDocuments,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { vesselId } = useParams()

  const { data: VesselData } = useGetVessel({ vesselId })

  const allowEditDocument =
    useCanEditAndAddDocument() && document.readOnly !== true

  if (!VesselData?.vesselName || !vesselId) {
    return null
  }

  return (
    <>
      {allowEditDocument && (
        <IconButton
          size="small"
          onClick={() => setIsModalOpen(true)}
          aria-label="edit/add document"
          sx={{ color: COLORS.secondary.darkBlue }}
        >
          {canAddDocument ? <AddIcon size={20} /> : <EditIcon size={20} />}
        </IconButton>
      )}
      {isModalOpen && (
        <DocumentsEditModal
          refetch={refetch}
          vessel={{
            vesselName: VesselData.vesselName,
            vesselId: vesselId,
          }}
          close={() => {
            setIsModalOpen(false)
            close?.()
          }}
          document={document}
          existingDocumentTags={existingDocumentTags}
          showTechManagerDocuments={showTechManagerDocuments}
        />
      )}
    </>
  )
}

export function DocumentsEditModal({
  close,
  document,
  vessel,
  flow,
  refetch,
  existingDocumentTags = [],
  showTechManagerDocuments,
}: {
  close: () => void
  document: IDocumentsRowData
  vessel: {
    vesselName: string
    vesselId: string
  }
  flow?: 'add' | 'edit'
  refetch?: () => void
  existingDocumentTags?: string[]
  showTechManagerDocuments: boolean
}) {
  const dateFormat = useDateFormat('numeric - short')
  const { vesselId, vesselName } = vessel
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const { setStatus } = useAnimatedAlert()
  const deleteDocumentMutation = useDeleteDocument()
  const {
    mutate: postDocument,
    isError: isErrorPostDocument,
    error: postDocumentError,
    isSuccess: isSuccessPostDocument,
    isPending: isPendingPostDocument,
  } = usePostDocuments({
    vesselId: vessel.vesselId,
    refetch,
  })

  useEffect(() => {
    if (isErrorPostDocument) {
      const invalidInput = invalidInputMessage(postDocumentError)
      setStatus(invalidInput ?? 'Updating document failed', 5000, 'error')
      // Keep the modal open so the invalid value can be corrected.
      if (!invalidInput) close()
    }
  }, [isErrorPostDocument])

  useEffect(() => {
    if (isSuccessPostDocument) {
      setStatus('Document updated', 5000)
      gtm.pushEvent('document_updated', {
        document_name: document.documentName,
        document_category: document.type,
      })
      queryClient.invalidateQueries({
        queryKey: [`/vessels/${vesselId}/documents`],
      })
      close()
    }
  }, [isSuccessPostDocument])

  const hasDocument = !!document.files?.length
  const isQ88 = document?.source === 'Q88'
  const isDocumentNa = document?.notApplicable
  const documentName = document?.documentName

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (!data.notApplicable && !data.file && !hasDocument) {
      setStatus('Updating document failed', 5000, 'error')
      return
    }

    const formData = new FormData()
    formData.append('documentName', document.documentName)
    formData.append('type', document.type)
    formData.append('notApplicable', data.notApplicable ? 'true' : 'false')
    if (data.issued) {
      formData.append(
        'issued',
        forceDateToZuluTimezone(data.issued).toISOString(),
      )
    }
    if (data.expires) {
      formData.append(
        'expires',
        forceDateToZuluTimezone(data.expires).toISOString(),
      )
    }
    if (data.certificateNumber) {
      formData.append('certificateNumber', data.certificateNumber)
    }
    if (data.certificateIssuer) {
      formData.append('certificateIssuer', data.certificateIssuer)
    }
    if (data.referenceNumber) {
      formData.append('referenceNumber', data.referenceNumber)
    }
    if (data.lastSurvey) {
      formData.append(
        'lastSurvey',
        forceDateToZuluTimezone(data.lastSurvey).toISOString(),
      )
    }
    if (data.nextSurvey) {
      formData.append(
        'nextSurvey',
        forceDateToZuluTimezone(data.nextSurvey).toISOString(),
      )
    }
    if (data.remarks) {
      formData.append('remarks', data.remarks)
    }
    if (data.digitalCertificate !== undefined) {
      formData.append(
        'digitalCertificate',
        data.digitalCertificate ? 'true' : 'false',
      )
    }
    formData.append('noExpiryDate', data.noExpiryDate ? 'true' : 'false')
    formData.append('noNextSurvey', data.noNextSurvey ? 'true' : 'false')
    if (data.file) {
      const filePayload = new File(
        [data.file],
        data.file?.name?.replaceAll(',', ' '),
      )
      formData.append('file', filePayload)
    }
    if (data.certificateCategories?.length) {
      data.certificateCategories.forEach((category: string) => {
        formData.append('certificateCategories', category)
      })
    }
    if (data.tags?.length) {
      data.tags.forEach((tag: string) => {
        formData.append('tags', tag)
      })
    }

    postDocument(formData)
  }

  const methods = useForm()

  const save = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    methods.handleSubmit(onSubmit)()
  }

  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    (document as any).certificateCategories || [],
  )

  const isNaChecked = methods.watch('notApplicable')
  const fileInputValue = methods.watch('file')
  const issuedInputValue = methods.watch('issued')
  const expiryInputValue = methods.watch('expires')
  const noExpiryDateValue = methods.watch('noExpiryDate')

  const isNewNaValue = isNaChecked !== isDocumentNa
  let isFormValidToUpdate = isNewNaValue
  let isFormValidToUpload = isNewNaValue

  useMount(() => {
    methods.setValue('notApplicable', isDocumentNa)
    methods.setValue('digitalCertificate', document.digitalCertificate)
    methods.setValue('noExpiryDate', !!document.noExpiryDate)
    methods.setValue('noNextSurvey', !!document.noNextSurvey)
    // The API resets tags and categories to [] when they are absent from the payload.
    methods.setValue('tags', document.tags || [])
    methods.setValue(
      'certificateCategories',
      document.certificateCategories || [],
    )
  })

  if (!isNaChecked) {
    const isDirtyIssueDate =
      issuedInputValue !== '' &&
      document?.issued &&
      toDateFormat(issuedInputValue, dateFormat) !==
        toDateFormat(parseDateOnly(document.issued), dateFormat)
    const isDirtyExpiryDate =
      expiryInputValue !== '' &&
      document?.expires &&
      toDateFormat(expiryInputValue, dateFormat) !==
        toDateFormat(parseDateOnly(document.expires), dateFormat)

    const hasNewFile = Boolean(fileInputValue)
    const isDateRequired = getIsDateRequired(document.type)

    isFormValidToUpdate = isDirtyIssueDate || isDirtyExpiryDate || hasNewFile
    isFormValidToUpload =
      hasNewFile &&
      (isDateRequired ? !!issuedInputValue && !!expiryInputValue : true)
  }

  const isDisabled = useCallback(() => {
    if (showTechManagerDocuments) {
      const issued = methods.watch('issued')
      const expires = methods.watch('expires')
      const isDateRequired = getIsDateRequired(document.type)
      if (isDateRequired && !noExpiryDateValue && !expires) return true
      if (isDateRequired && !issued) return true

      return false
    }
    return !hasDocument || isQ88 ? !isFormValidToUpload : !isFormValidToUpdate
  }, [
    issuedInputValue,
    expiryInputValue,
    noExpiryDateValue,
    fileInputValue,
    isNaChecked,
    isFormValidToUpdate,
    isFormValidToUpload,
    hasDocument,
    isQ88,
    showTechManagerDocuments,
    document,
    selectedCategories,
  ])

  return (
    <DialogBox
      open
      onClose={() => close()}
      sx={{ width: '780px', maxWidth: 'none' }}
    >
      <Typography variant="h2" sx={{ mb: 4 }}>
        {`${isQ88 || flow === 'add' ? 'Add' : 'Edit'} ${documentName} for ${vesselName}`}
      </Typography>
      {isPendingPostDocument ? (
        <Box sx={{ height: '300px' }}>
          <CircularProgress aria-label="Loading…" />
        </Box>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={(e) => save(e)}>
            <TableValuesEditModalContent
              document={document}
              isNaChecked={isNaChecked}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              showTechManagerDocuments={showTechManagerDocuments}
              existingDocumentTags={existingDocumentTags}
            />
            <ModalFooter>
              <Button onClick={() => close()} type="button">
                Cancel
              </Button>
              {hasDocument && !isQ88 && (
                <TextButton
                  color="error"
                  onClick={() => setShowDeleteConfirmation(true)}
                  type="button"
                >
                  Delete document
                </TextButton>
              )}
              <Button variant="contained" type="submit" disabled={isDisabled()}>
                Save
              </Button>
            </ModalFooter>
          </form>
          <ConfirmModal
            header={`Delete ${document.documentName}?`}
            content="By clicking “yes” you confirm this document will be deleted. The document cannot be retrieved again."
            confirmButtonText="yes"
            cancelButtonText="cancel"
            isVisible={showDeleteConfirmation}
            onConfirm={() => {
              deleteDocumentMutation.mutate(
                { vesselId, documentName: document.documentName },
                {
                  onSuccess: (response) => {
                    if (
                      response?.statusCode === 401 ||
                      response?.statusCode === 403
                    ) {
                      setStatus(
                        "You're not authorized to perform this action.",
                        5000,
                        'error',
                      )
                      return
                    }
                    if (response?.files) {
                      setStatus('Error occurred', 5000, 'error')
                      return
                    }
                    setStatus('Document deleted', 5000)
                    gtm.pushEvent('document_deleted', {
                      document_name: document.documentName,
                      document_category: document.type,
                    })
                    refetch?.()
                    close()
                  },
                  onError: (error) => {
                    setStatus(
                      'Error ' +
                        ((error as any)?.statusCode || '') +
                        ' occurred',
                      5000,
                      'error',
                    )
                  },
                },
              )
            }}
            onClose={() => setShowDeleteConfirmation(false)}
          />
        </FormProvider>
      )}
    </DialogBox>
  )
}
